import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { toast } from "react-toastify";
import { Tool } from "features/Tools/types/tool.types";
import { deleteTool, getTools } from "features/Tools/services/ToolService";
import { getDocumentTitle } from "utils/helpers";
import { ToolCreateModal } from "../ToolForm/ToolCreateModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import ToolListFilters, { ToolStatusFilter } from "./ToolListFilters";

const ToolList = () => {
  document.title = getDocumentTitle("Tools");
  const { can } = useAccess();
  const canUpdateTool = can(Permissions.TOOL.UPDATE);
  const canDeleteTool = can(Permissions.TOOL.DELETE);

  const tableRef = useRef<() => void>(null);
  const [selected, setSelected] = useState<Tool | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toolModalOpen, setToolModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ToolStatusFilter>("");

  const requestParams = useMemo(
    () =>
      statusFilter
        ? { isActive: statusFilter === "active" }
        : {},
    [statusFilter],
  );

  const filtersBadgeCount = useMemo(
    () => (statusFilter ? 1 : 0),
    [statusFilter],
  );

  const clearListFilters = useCallback(() => setStatusFilter(""), []);

  const openCreate = useCallback(() => {
    setToolModalOpen(true);
  }, []);

  const closeToolModal = useCallback(() => {
    setToolModalOpen(false);
  }, []);

  const columns: ColumnDef<Tool>[] = useMemo(
    () => [
      IndexColumn<Tool>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.name}</span>
        ),
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const active = row.original.isActive;
          return (
            <span
              className={`badge bg-${active ? "success" : "secondary"}-subtle text-${active ? "success" : "secondary"} rounded-pill`}
            >
              {active ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const hasMenuItems = canUpdateTool || canDeleteTool;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Tool actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canUpdateTool ? (
                  <DropdownItem
                    tag={Link}
                    to={`/inventory-management/tools/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDeleteTool ? (
                  <>
                    {canUpdateTool ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => {
                        setSelected(r);
                        setDeleteOpen(true);
                      }}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDeleteTool, canUpdateTool],
  );

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteTool(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
      setDeleteOpen(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    }
  };

  return (
    <React.Fragment>
      <ToolCreateModal
        show={toolModalOpen}
        editingTool={null}
        onClose={closeToolModal}
        onSuccess={() => tableRef.current?.()}
      />
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.name}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tools" pageTitle="Inventory Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Tools</h5>
                <p className="text-muted small mb-0">
                  Manage tools available for checkout. Names must be unique.
                </p>
              </div>
              {can(Permissions.TOOL.CREATE) ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreate}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add Tool
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getTools}
                requestParams={requestParams}
                pageSize={10}
                globalSearch
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                SearchPlaceholder="Search tools by name…"
                toolbarRight={
                  <ToolListFilters
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                  />
                }
                filterCount={filtersBadgeCount}
                onFiltersClear={clearListFilters}
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ToolList;
